import React from 'react';
import { Link } from '@reach/router';
import { compose, withHandlers, lifecycle } from 'recompose';
import { connect } from '../../redux/store';
import {
  I18n,
  DatastoreSubmissionTable,
  Moment,
  mountTable,
  unmountTable,
  refetchTable,
} from '@kineticdata/react';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import {
  PaginationBar,
  TableComponents,
  addToast,
  addToastAlert,
  openConfirm,
} from '@kineticdata/bundle-common';
import { PageTitle } from '../shared/PageTitle';
import { actions, ROBOT_FORM_SLUG } from '../../redux/modules/settingsRobots';
import moment from 'moment';

const NameCell = ({ row }) => (
  <td>
    <Link to={row.get('id')}>{row.getIn(['values', 'Robot Name'])}</Link>
  </td>
);

const StatusCell = ({ row }) => {
  const isExpired =
    row.getIn(['values', 'End Date']) &&
    moment(row.getIn(['values', 'End Date'])).isBefore(moment());
  return (
    <td>
      <TableComponents.StatusBadge status={row.getIn(['values', 'Status'])} />
      {isExpired && <TableComponents.StatusBadge status={'Expired'} />}
    </td>
  );
};

const getNextExecution = (nextExecutions, robotId) => {
  let nextExecution;
  const found = nextExecutions.find(
    execution => execution.values['Robot ID'] === robotId,
  );

  if (found) {
    nextExecution = found.values['Next Execution'] ? (
      <Moment
        timestamp={found.values['Next Execution']}
        format={Moment.formats.dateTime}
      />
    ) : (
      'No upcoming executions scheduled'
    );
  } else {
    nextExecution = 'Unknown';
  }

  return nextExecution;
};

const EmptyBodyRow = TableComponents.generateEmptyBodyRow({
  loadingMessage: 'Loading Robots...',
  noSearchResultsMessage:
    'No robots were found - please modify your search criteria',
  noItemsMessage: 'There are no robots to display',
});

const generatePaginationControl = ({ pageSize, changePageSize }) => props => (
  <PaginationBar
    className="mb-3"
    paging={props.loading}
    previousPage={props.prevPage}
    nextPage={props.nextPage}
    startIndex={props.startIndex}
    endIndex={props.endIndex}
    count={props.count}
    pageSize={pageSize}
    changePageSize={changePageSize}
  />
);

export const RobotsListComponent = ({
  tableKey,
  nextExecutions,
  nextExecutionsError,
  pageSize,
  changePageSize,
  handleClone,
  handleDelete,
}) => {
  const ExecutionsCell = ({ row }) => {
    const nextExecution = nextExecutions
      ? getNextExecution(nextExecutions, row.get('id'))
      : nextExecutionsError
        ? 'Error'
        : 'Fetching...';
    return <td>{nextExecution}</td>;
  };

  const ActionsCell = ({ row }) => (
    <td>
      <UncontrolledDropdown>
        <DropdownToggle tag="button" className="btn btn-sm btn-link">
          <span className="sr-only">More Actions</span>
          <span className="fa fa-angle-down fa-fw" role="presentation" />
        </DropdownToggle>
        <DropdownMenu right positionFixed>
          <DropdownItem tag={Link} to={row.get('id')}>
            <I18n>Edit</I18n>
          </DropdownItem>
          <DropdownItem onClick={handleClone(row.get('id'))}>
            <I18n>Clone</I18n>
          </DropdownItem>
          <DropdownItem tag={Link} to={`${row.get('id')}/executions`}>
            <I18n>View Executions</I18n>
          </DropdownItem>
          <DropdownItem divider />
          <DropdownItem
            onClick={handleDelete(row.get('id'))}
            className="text-danger"
          >
            <I18n>Delete</I18n>
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </td>
  );

  const PaginationControl = generatePaginationControl({
    pageSize,
    changePageSize,
  });

  return (
    <DatastoreSubmissionTable
      tableKey={tableKey}
      formSlug={ROBOT_FORM_SLUG}
      include="values"
      components={{
        EmptyBodyRow,
        TableLayout: TableComponents.SettingsTableLayout,
        PaginationControl,
      }}
      columnSet={[
        'robotName',
        'status',
        'category',
        'treeName',
        'description',
        'nextExecutionTime',
        'actions',
      ]}
      defaultSortColumn={'robotName'}
      defaultSortDirection={'asc'}
      addColumns={[
        {
          value: 'robotName',
          title: 'Robot Name',
          sortable: true,
          components: {
            BodyCell: NameCell,
          },
        },
        {
          value: 'status',
          title: 'Status',
          components: {
            BodyCell: StatusCell,
          },
        },
        {
          value: 'category',
          valueTransform: (_value, row) => row.getIn(['values', 'Category']),
          title: 'Category',
        },
        {
          value: 'treeName',
          valueTransform: (_value, row) => row.getIn(['values', 'Task Tree']),
          title: 'Tree Name',
        },
        {
          value: 'description',
          valueTransform: (_value, row) => row.getIn(['values', 'Description']),
          title: 'Description',
        },
        {
          value: 'nextExecutionTime',
          title: 'Next Execution Time',
          components: {
            BodyCell: ExecutionsCell,
          },
        },
        {
          value: 'actions',
          title: ' ',
          components: {
            BodyCell: ActionsCell,
          },
        },
      ]}
      initialFilterValues={{
        query: {
          index: 'values[Robot Name]',
        },
      }}
      pageSize={pageSize}
    >
      {({ pagination, table }) => (
        <div className="page-container">
          <div className="page-panel">
            <PageTitle
              parts={['Robots']}
              breadcrumbs={[]}
              title="Robots"
              actions={[
                {
                  label: 'Create Robot',
                  icon: 'plus',
                  to: 'new',
                },
              ]}
            />
            <div className="scroll-wrapper-h">{table}</div>
            {pagination}
          </div>
        </div>
      )}
    </DatastoreSubmissionTable>
  );
};

const mapStateToProps = state => ({
  nextExecutions: state.settingsRobots.nextExecutions,
  nextExecutionsError: state.settingsRobots.nextExecutionsError,
});

const mapDispatchToProps = {
  fetchNextExecutions: actions.fetchNextExecutionsRequest,
  cloneRobot: actions.cloneRobotRequest,
  deleteRobot: actions.deleteRobotRequest,
};

const handleClone = props => id => () =>
  props.cloneRobot({
    id: id,
    success: submission => {
      addToast(`Robot cloned successfully`);
      refetchTable(props.tableKey);
      props.navigate(submission.id);
    },
    failure: error =>
      addToastAlert({ title: 'Clone Failed', message: error.message }),
  });

const handleDelete = props => id => () =>
  openConfirm({
    title: 'Delete Robot',
    body: 'Are you sure you want to delete this Robot?',
    actionName: 'Delete',
    ok: () => {
      props.deleteRobot({
        id: id,
        success: () => {
          addToast(`Robot deleted successfully`);
          refetchTable(props.tableKey);
        },
        failure: error =>
          addToastAlert({ title: 'Delete Failed', message: error.message }),
      });
    },
  });

export const RobotsList = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withHandlers({
    handleClone,
    handleDelete,
    changePageSize: props =>
      !!props.setPageSize
        ? value => () => {
            props.setPageSize(value);
            unmountTable(props.tableKey);
            mountTable(props.tableKey);
          }
        : undefined,
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchNextExecutions();
    },
  }),
)(RobotsListComponent);
